import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

import { cx } from '../../lib/cx';
import { Button } from '../Button';

import styles from './ConfirmDialog.module.css';

export interface ConfirmDialogProps {
  open: boolean;
  /** The question, in a few words: "Remove Anna from your students?" */
  title: string;
  /** What follows from saying yes. */
  children?: ReactNode;
  /** The action by its own name ("Remove", "Leave"), not "OK". */
  confirmLabel: string;
  cancelLabel: string;
  /** The action cannot be taken back: its button says so by its colour. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  className?: string;
}

/**
 * A question that stands between a person and an action they may regret. It lies
 * over the page and takes the keyboard: focus starts on the way out, Tab stays
 * inside, Escape and a press beside it mean no. When it closes, focus goes back to
 * where it was.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel,
  destructive = false,
  onConfirm,
  onCancel,
  className,
}: ConfirmDialogProps) {
  const id = useId();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const before = document.activeElement;
    // The way out is the first button: a key pressed by habit does no harm.
    panel.current?.querySelector('button')?.focus();
    return () => {
      if (before instanceof HTMLElement) before.focus();
    };
  }, [open]);

  if (!open) return null;

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onCancel();
      return;
    }
    if (event.key !== 'Tab') return;
    const buttons = [...(panel.current?.querySelectorAll('button') ?? [])];
    const first = buttons[0];
    const last = buttons.at(-1);
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className={styles.backdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div
        ref={panel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={children ? `${id}-text` : undefined}
        className={cx(styles.panel, className)}
        onKeyDown={onKeyDown}
      >
        <h2 id={`${id}-title`} className={styles.title}>
          {title}
        </h2>
        {children ? (
          <div id={`${id}-text`} className={styles.text}>
            {children}
          </div>
        ) : null}
        <div className={styles.actions}>
          <Button variant="secondary" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button className={destructive ? styles.destructive : undefined} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
